import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Copy, CreditCard, Loader2, QrCode, Receipt } from 'lucide-react';
import { GIFT_COPY, parseBRL } from '@garlic/shared';
import { api, ApiError, errorMessage, post } from '../../lib/api';
import { formatBRL } from '../../lib/format';
import { loadMercadoPago, type CardFormData } from '../../lib/mercadopago';
import { useGuestRealtime } from '../../lib/realtime';
import { Alert, Badge, Button, Card, Input, Spinner, cx } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { MoneyInput } from '../../ui/controls';
import type { TenantRestaurant } from '../TenantApp';
import { publicPath } from '../../lib/paths';
import { GiftCard } from './GiftCard';
import { ItemUnitsPicker, selectionCents, selectionPayload, type PayableItem, type UnitSelection } from '../ItemUnitsPicker';

type Item = PayableItem & { orderNumber: number };
type AccountView = {
  consumedCents: number; discountCents: number; paidCents: number; processingCents: number; dueCents: number; availableCents: number; overpaidCents: number; settled: boolean;
  items: Item[];
  payments: { id: string; kind: string; method: string; amountCents: number; status: string; createdAt: string }[];
  processing: { id: string; method: string; amountCents: number; status: string; mine: boolean; expiresAt: string }[];
};
type AccountResponse = {
  session: { number: number; tableLabel: string };
  account: AccountView;
  online: { available: boolean; methods: string[]; publicKey: string | null; testMode: boolean };
  gift: { enabled: boolean; reviewUrl: string | null };
};
type Attempt = { id: string; method: 'PIX' | 'CARD'; amountCents: number; status: string; expiresAt: string; pix: { qrCode: string; qrCodeBase64: string | null; ticketUrl: string | null } | null; message: string | null };

const METHOD: Record<string, string> = { PIX: 'Pix', CARD: 'Cartão', CASH: 'Dinheiro', CARD_TERMINAL: 'Maquininha', PIX_MANUAL: 'Pix (caixa)', OTHER: 'Outro' };

export function PayBill({ restaurant }: { restaurant: TenantRestaurant }) {
  const q = useQuery({ queryKey: ['guest-account'], queryFn: () => api<AccountResponse>('/api/public/account'), retry: false, refetchInterval: 30_000 });
  // Eventos da comanda (pagamento confirmado por outra pessoa, novo pedido…) atualizam a conta em tempo real.
  useGuestRealtime(q.isSuccess);
  const qc = useQueryClient();
  const [attempt, setAttempt] = useState<Attempt | null>(null);

  if (q.isPending) return <Spinner />;
  if (q.isError) {
    return (
      <main className="grid min-h-dvh place-items-center px-4 text-center">
        <div className="max-w-sm">
          <QrCode className="mx-auto size-8 text-ink-3" />
          <p className="mt-3 font-semibold">{q.error instanceof ApiError && q.error.status === 410 ? 'Esta comanda foi encerrada' : 'Nenhuma mesa identificada'}</p>
          <Link to={publicPath()} className="mt-4 inline-block text-sm text-[var(--brand)] underline">Voltar ao cardápio</Link>
        </div>
      </main>
    );
  }
  const { account: a, online, session } = q.data;

  return (
    <div className="mx-auto min-h-dvh max-w-2xl px-4 pb-16 pt-4">
      <Link to={publicPath('/mesa')} className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink"><ArrowLeft className="size-4" /> Minha mesa</Link>
      <h1 className="mt-3 font-display text-2xl font-semibold">Conta da mesa {session.tableLabel}</h1>
      <p className="text-sm text-ink-3">{restaurant.name} · comanda nº {session.number}</p>

      <Card className="mt-5 divide-y divide-line">
        <Row label="Consumido" value={a.consumedCents} />
        {a.discountCents > 0 && <Row label="Descontos" value={-a.discountCents} />}
        <Row label="Já pago" value={-a.paidCents} />
        {a.processingCents > 0 && <Row label="Em processamento" value={a.processingCents} muted />}
        <div className="flex items-baseline justify-between px-5 py-4">
          <span className="font-semibold">{a.settled ? 'Conta paga' : 'Falta pagar'}</span>
          <span className={cx('font-display text-3xl font-semibold tabular-nums', a.settled && 'text-sprout-600')}>{formatBRL(a.dueCents)}</span>
        </div>
      </Card>

      {a.settled ? (
        <div className="mt-5">
          <Alert tone="success" title="CONTA PAGA">Obrigado! Mostre o comprovante de saída ao balconista.</Alert>
          <Link to={publicPath('/saida')} className="mt-3 block">
            <Button variant="brand" size="lg" className="w-full" icon={<Receipt className="size-5" />}>Ver comprovante de saída</Button>
          </Link>
        </div>
      ) : attempt ? (
        <AttemptPanel attempt={attempt} publicKey={online.publicKey} gift={q.data.gift} onDone={() => { setAttempt(null); void qc.invalidateQueries({ queryKey: ['guest-account'] }); }} onUpdate={setAttempt} />
      ) : !online.available ? (
        <div className="mt-5"><Alert tone="info" title="Pagamento com a equipe">O pagamento pelo celular não está disponível neste restaurante. Peça a conta a um atendente.</Alert></div>
      ) : (
        <PaySelector account={a} methods={online.methods} testMode={online.testMode} onStarted={setAttempt} />
      )}

      {a.processing.filter((p) => p.mine && !attempt).map((p) => (
        <div key={p.id} className="mt-4">
          <Alert tone="warning">Você tem um pagamento de {formatBRL(p.amountCents)} em processamento.</Alert>
          {/* Depois de recarregar a página: volta ao formulário do cartão ou ao QR do Pix (ou deixa cancelar). */}
          <ResumeAttempt id={p.id} onResume={setAttempt} />
        </div>
      ))}

      {a.payments.length > 0 && (
        <>
          <h2 className="mt-8 font-semibold">Pagamentos</h2>
          <ul className="mt-2 divide-y divide-line rounded-2xl border border-line bg-white text-sm">
            {a.payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span>{METHOD[p.method] ?? p.method}{p.kind === 'MANUAL' && <span className="text-ink-3"> · registrado pela equipe</span>}</span>
                <span className={cx('tabular-nums', p.status === 'REVERSED' && 'text-ink-3 line-through')}>{formatBRL(p.amountCents)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className={cx('flex justify-between px-5 py-2.5 text-sm', muted && 'text-warn-600')}>
      <span className={muted ? '' : 'text-ink-3'}>{label}</span>
      <span className="tabular-nums">{value < 0 ? `− ${formatBRL(-value)}` : formatBRL(Math.abs(value))}</span>
    </div>
  );
}

function PaySelector({ account: a, methods, testMode, onStarted }: { account: AccountView; methods: string[]; testMode: boolean; onStarted: (a: Attempt) => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<'TOTAL' | 'ITEMS' | 'AMOUNT'>('TOTAL');
  const [selected, setSelected] = useState<UnitSelection>({});
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'PIX' | 'CARD'>(methods.includes('PIX') ? 'PIX' : 'CARD');
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem('garlic:email') ?? '';
    } catch {
      return '';
    }
  });
  const [cpf, setCpf] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const keyRef = useRef<string | null>(null);

  const payable = a.items.filter((i) => !i.cancelled && i.remainingCents > 0);
  const selectedTotal = selectionCents(payable, selected);
  const amountCents = mode === 'TOTAL' ? a.availableCents : mode === 'ITEMS' ? selectedTotal : parseBRL(amount) ?? 0;
  const valid = amountCents >= 100 && amountCents <= a.availableCents && (method === 'CARD' || /\S+@\S+\.\S+/.test(email));

  const start = async () => {
    setBusy(true);
    setError(undefined);
    keyRef.current ??= crypto.randomUUID();
    try {
      try {
        localStorage.setItem('garlic:email', email);
      } catch {
        /* sem armazenamento */
      }
      const r = await post<Attempt>('/api/public/payments', {
        idempotencyKey: keyRef.current,
        method,
        mode,
        ...(mode === 'ITEMS' ? { itemUnits: selectionPayload(selected) } : {}),
        ...(mode === 'AMOUNT' ? { amountCents } : {}),
        ...(method === 'PIX' ? { payerEmail: email, payerCpf: cpf } : {}),
      });
      keyRef.current = null;
      onStarted(r);
    } catch (e) {
      if (e instanceof ApiError && e.status < 500) keyRef.current = null;
      setError(errorMessage(e));
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (a.availableCents <= 0) return <div className="mt-5"><Alert tone="warning">O restante da conta está em processamento por outra pessoa da mesa. Aguarde a confirmação.</Alert></div>;

  return (
    <Card className="mt-5 p-5">
      <h2 className="font-semibold">Como você quer pagar?</h2>
      {testMode && <p className="mt-1"><Badge tone="warning">Modo de teste do Mercado Pago — nenhuma cobrança real</Badge></p>}
      <div role="radiogroup" className="mt-3 grid grid-cols-3 gap-1 rounded-xl bg-paper-2 p-1 text-sm">
        {([['TOTAL', 'Tudo'], ['ITEMS', 'Meus itens'], ['AMOUNT', 'Outro valor']] as const).map(([k, l]) => (
          <button key={k} role="radio" aria-checked={mode === k} onClick={() => setMode(k)} className={cx('rounded-lg py-2 font-medium', mode === k ? 'bg-white shadow-sm' : 'text-ink-3')}>{l}</button>
        ))}
      </div>

      {mode === 'ITEMS' && (
        <ItemUnitsPicker className="mt-4" items={a.items.filter((i) => !i.cancelled)} value={selected} onChange={setSelected} showStatus />
      )}
      {mode === 'AMOUNT' && (
        <div className="mt-4">
          <label htmlFor="pay-amount" className="text-sm font-medium text-ink-2">Quanto você quer pagar? (até {formatBRL(a.availableCents)})</label>
          <div className="mt-1.5"><MoneyInput id="pay-amount" value={amount} onChange={setAmount} invalid={!!amount && (amountCents > a.availableCents || amountCents < 100)} /></div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-2">
        {methods.includes('PIX') && (
          <button onClick={() => setMethod('PIX')} className={cx('flex items-center justify-center gap-2 rounded-xl border-2 py-3 font-medium', method === 'PIX' ? 'border-[var(--brand)] bg-white' : 'border-line bg-paper text-ink-3')}>
            <QrCode className="size-5" /> Pix
          </button>
        )}
        {methods.includes('CARD') && (
          <button onClick={() => setMethod('CARD')} className={cx('flex items-center justify-center gap-2 rounded-xl border-2 py-3 font-medium', method === 'CARD' ? 'border-[var(--brand)] bg-white' : 'border-line bg-paper text-ink-3')}>
            <CreditCard className="size-5" /> Cartão
          </button>
        )}
      </div>

      {method === 'PIX' && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="pay-email" className="text-sm font-medium text-ink-2">Seu e-mail</label>
            <Input id="pay-email" type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Exigido pelo Mercado Pago" />
          </div>
          <div>
            <label htmlFor="pay-cpf" className="text-sm font-medium text-ink-2">CPF (opcional)</label>
            <Input id="pay-cpf" inputMode="numeric" className="mt-1.5" value={cpf} onChange={(e) => setCpf(e.target.value)} />
          </div>
          <p className="text-xs text-ink-3 sm:col-span-2">Usados só para gerar o Pix no Mercado Pago. O Garlic não guarda esses dados.</p>
        </div>
      )}

      {error && <div className="mt-4"><Alert tone="danger">{error}</Alert></div>}
      <Button variant="brand" size="lg" className="mt-5 w-full" disabled={!valid} loading={busy} onClick={start}>
        {method === 'PIX' ? 'Gerar Pix' : 'Continuar para o cartão'} · {formatBRL(Math.max(0, amountCents))}
      </Button>
      {amountCents > 0 && amountCents < 100 && <p className="mt-2 text-center text-xs text-ink-3">Valor mínimo pelo celular: R$ 1,00.</p>}
    </Card>
  );
}

function ResumeAttempt({ id, onResume }: { id: string; onResume: (a: Attempt) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try {
      onResume(await api<Attempt>(`/api/public/payments/${id}`));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return <Button variant="secondary" className="mt-2 w-full" loading={busy} onClick={resume}>Continuar este pagamento</Button>;
}

/** Acompanha a tentativa: QR do Pix ou formulário de cartão; confirmação só quando o servidor disser "aprovado". */
function AttemptPanel({ attempt, publicKey, gift, onDone, onUpdate }: { attempt: Attempt; publicKey: string | null; gift: AccountResponse['gift']; onDone: () => void; onUpdate: (a: Attempt) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // Consulta o servidor (que consulta o Mercado Pago) enquanto estiver em processamento.
  useEffect(() => {
    if (attempt.status !== 'PENDING') return;
    const t = setInterval(async () => {
      try {
        const r = await api<Attempt>(`/api/public/payments/${attempt.id}`);
        if (r.status !== attempt.status) {
          onUpdate(r);
          void qc.invalidateQueries({ queryKey: ['guest-account'] });
        }
      } catch {
        /* mantém o último estado */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [attempt.id, attempt.status, onUpdate, qc]);

  const secondsLeft = Math.max(0, Math.floor((new Date(attempt.expiresAt).getTime() - now) / 1000));
  const cancel = async () => {
    try {
      onUpdate(await post<Attempt>(`/api/public/payments/${attempt.id}/cancel`));
      void qc.invalidateQueries({ queryKey: ['guest-account'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (attempt.status === 'APPROVED') {
    return (
      <>
        <Card className="mt-5 p-6 text-center">
          <CheckCircle2 className="mx-auto size-10 text-sprout-600" />
          <p className="mt-2 text-lg font-semibold">Pagamento confirmado</p>
          <p className="text-sm text-ink-3">{formatBRL(attempt.amountCents)} via {attempt.method === 'PIX' ? 'Pix' : 'cartão'}.</p>
          <Button className="mt-4" variant="brand" onClick={onDone} icon={<Receipt className="size-4" />}>Ver conta</Button>
        </Card>
        {/* Quem pagou só a sua parte também é convidado a avaliar e ganha o brinde. */}
        {gift.enabled && <GiftCard className="mt-4" reviewUrl={gift.reviewUrl} thanksText={GIFT_COPY.thanksPartial} />}
      </>
    );
  }
  if (['REJECTED', 'CANCELLED', 'EXPIRED', 'REFUNDED'].includes(attempt.status)) {
    return (
      <Card className="mt-5 p-6 text-center">
        <p className="font-semibold">{attempt.status === 'REJECTED' ? 'Pagamento recusado' : attempt.status === 'EXPIRED' ? 'O Pix expirou' : 'Pagamento cancelado'}</p>
        {attempt.message && <p className="mt-1 text-sm text-ink-3">{attempt.message}</p>}
        <Button className="mt-4" variant="secondary" onClick={onDone}>Tentar de novo</Button>
      </Card>
    );
  }
  if (attempt.method === 'CARD' && attempt.status === 'CREATED') {
    return <CardBrick attempt={attempt} publicKey={publicKey} onResult={(a) => { onUpdate(a); void qc.invalidateQueries({ queryKey: ['guest-account'] }); }} onCancel={cancel} />;
  }
  return (
    <Card className="mt-5 p-5">
      {attempt.pix ? (
        <>
          <p className="text-center font-semibold">Pague {formatBRL(attempt.amountCents)} com Pix</p>
          <p className="text-center text-xs text-ink-3">Abra o app do seu banco e escaneie, ou use o copia e cola. Expira em {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}.</p>
          {attempt.pix.qrCodeBase64 && <img src={`data:image/jpeg;base64,${attempt.pix.qrCodeBase64}`} alt="QR Code Pix" className="mx-auto mt-4 size-56 rounded-xl border border-line bg-white p-2" />}
          <div className="mt-4 flex gap-2">
            <Input readOnly value={attempt.pix.qrCode} className="font-mono text-xs" aria-label="Pix copia e cola" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="secondary" icon={<Copy className="size-4" />} onClick={() => navigator.clipboard.writeText(attempt.pix!.qrCode).then(() => toast.success('Código Pix copiado'))}>Copiar</Button>
          </div>
        </>
      ) : (
        <p className="text-center font-semibold">Pagamento de {formatBRL(attempt.amountCents)} em processamento</p>
      )}
      <p className="mt-4 flex items-center justify-center gap-2 text-sm text-ink-3"><Loader2 className="size-4 animate-spin" /> Aguardando confirmação do Mercado Pago…</p>
      {attempt.message && <p className="mt-2 text-center text-xs text-warn-600">{attempt.message}</p>}
      {attempt.pix && <Button variant="ghost" className="mt-3 w-full" onClick={cancel}>Desistir deste Pix</Button>}
    </Card>
  );
}

function CardBrick({ attempt, publicKey, onResult, onCancel }: { attempt: Attempt; publicKey: string | null; onResult: (a: Attempt) => void; onCancel: () => void }) {
  const [error, setError] = useState<string>();
  const [ready, setReady] = useState(false);
  const containerId = useMemo(() => `card-brick-${attempt.id}`, [attempt.id]);
  // O pai re-renderiza a cada segundo (relógio do Pix) e recria `onResult`: guardado num ref para que isso
  // não desmonte e remonte o formulário do Mercado Pago (era o "fica recarregando").
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!publicKey) return setError('Pagamento com cartão indisponível.');
    let controller: { unmount: () => void } | null = null;
    let cancelled = false;
    let loaded = false;
    setError(undefined);
    setReady(false);
    // Se o Mercado Pago não responder, mostra o erro com "tentar de novo" em vez de girar para sempre.
    const slow = setTimeout(() => {
      if (!loaded && !cancelled) setError('O formulário do cartão não carregou. Verifique sua internet e tente de novo.');
    }, 25_000);
    loadMercadoPago()
      .then(async (MP) => {
        if (cancelled) return;
        document.getElementById(containerId)?.replaceChildren(); // sobra de uma tentativa que falhou
        const mp = new MP(publicKey, { locale: 'pt-BR' });
        const created = await mp.bricks().create('cardPayment', containerId, {
          initialization: { amount: attempt.amountCents / 100 },
          customization: { paymentMethods: { maxInstallments: 1 }, visual: { style: { theme: 'default' } } },
          callbacks: {
            onReady: () => {
              loaded = true;
              setReady(true);
            },
            onError: (e: { message?: string }) => setError(e?.message ?? 'Erro no formulário de cartão'),
            // Só o token do cartão vai para o Garlic; o valor cobrado é o da tentativa, definido no servidor.
            onSubmit: async (form: CardFormData, extra?: { paymentTypeId?: string }) => {
              const r = await post<Attempt>(`/api/public/payments/${attempt.id}/card`, {
                token: form.token,
                paymentMethodId: form.payment_method_id,
                paymentType: extra?.paymentTypeId === 'debit_card' ? 'debit_card' : 'credit_card',
                installments: form.installments ?? 1,
                payerEmail: form.payer.email,
                // Só tipo e número do documento, repassados ao Mercado Pago (o Garlic não guarda).
                identification: form.payer.identification ? { type: form.payer.identification.type, number: form.payer.identification.number } : undefined,
                deviceId: window.MP_DEVICE_SESSION_ID,
              });
              onResultRef.current(r);
            },
          },
        });
        // Desmontado enquanto o formulário era criado (ex.: StrictMode, troca de tela): não deixa um formulário órfão.
        if (cancelled) created.unmount();
        else controller = created;
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
      clearTimeout(slow);
      controller?.unmount();
    };
  }, [attempt.id, attempt.amountCents, publicKey, containerId, retry]);
  return (
    <Card className="mt-5 p-5">
      <p className="font-semibold">Pagar {formatBRL(attempt.amountCents)} com cartão</p>
      <p className="text-xs text-ink-3">Os dados do cartão são digitados no formulário seguro do Mercado Pago e não passam pelo Garlic.</p>
      {error && (
        <div className="mt-3">
          <Alert tone="danger">{error}</Alert>
          {!ready && publicKey && <Button variant="secondary" className="mt-3 w-full" onClick={() => setRetry((n) => n + 1)}>Tentar de novo</Button>}
        </div>
      )}
      {!ready && !error && <Spinner label="Carregando formulário seguro…" />}
      <div id={containerId} className="mt-3" />
      <Button variant="ghost" className="mt-3 w-full" onClick={onCancel}>Cancelar</Button>
    </Card>
  );
}
